import moment from 'moment';
import path from 'path';
import url from 'url';
import { v4 as uuidv4 } from 'uuid';
import queryString from 'query-string';
import shorthash from 'shorthash';
import pluralize from 'pluralize';
import lodashOmit from 'lodash/fp/omit';
import lodashInvert from 'lodash/fp/invert';
import lodashCompact from 'lodash/fp/compact';

import {
  hasRoleAdmin,
} from 'common/userRoles';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import pickWithUndefinedDefault from 'common/util/pickWithUndefinedDefault';
import typeCheck from 'common/util/typeCheck';
import slugify from 'common/util/slugify';
import objectDiff from 'common/util/objectDiff';
import isBoolean from 'common/util/isBoolean';
import capitalizeAll from 'common/util/capitalizeAll';
import { convertStateCodeToStateName } from 'common/util/stateCodes';
import {
  LISTING_DATA_SOURCE_SUNROOM,
  LISTING_DATA_SOURCE_SMART_APARTMENT,
  LISTING_MOVE_IN_DATE_WARNING_THRESHOLD,
  LISTING_MOVE_IN_DATE_EXTENDED_DAYS,
  LISTING_STATUS_ACTIVE,
  LISTING_STATUS_PENDING,
  LISTING_AMENITIES_SETTINGS,
  LISTING_AMENITIES_LIST,
  LISTING_WHO_COLLECTS_LISTING_FEES_VALUE_MANUAL,
  LISTING_WHO_COLLECTS_LISTING_FEES_VALUE_SUNROOM,
  LISTING_SERVICE_TYPE_TENANT_SCREENING,
  LISTING_SERVICE_TYPE_SUNRISE,
  LISTING_SERVICE_TYPE_PRE_LEASE,
  LISTING_OCCUPANT_TYPE_VACANT,
} from 'common/listingConstants';
import {
  PROPERTY_TYPES_SINGLE_FAMILY,
} from 'common/propertyTypes';
import {
  PROPERTY_SETUP_ACCESS_TYPE_USE_ACCESS_CODE,
  PROPERTY_SETUP_ACCESS_TYPE_COMBO_LOCKBOX,
  PROPERTY_SETUP_ACCESS_TYPE_SUPRA_LOCKBOX,
  PROPERTY_SETUP_ACCESS_TYPE_CONTACT_FOR_KEY,
  MARKETING_PARTNER_LISTING_AGREEMENT_SIGNED_BY_SUNROOM,
} from 'common/marketingPartnerListingConstants';

import {
  SELF_SHOWING_LOCK_TYPE_IGLOOHOMELOCK,
  SELF_SHOWING_LOCK_TYPE_SUNLOCK,
  SELF_SHOWING_LOCK_TYPE_CODEBOX,
} from 'common/selfShowingLockConstants';

import {
  TOUR_STATUS_REQUESTED,
  TOUR_STATUS_SCHEDULED,
  TOUR_STATUS_COMPLETED,
} from 'common/tourConstants';
import { PET_WEIGHT_OPTION_ITEM_NONE } from 'client/helpers/petWeightOptionItems';
import {
  pickPartnerListingFeeSettingsFieldgroup,
  ownerPaysFor as ownerPaysForFieldgroup,
  booleanAmenitiesFieldgroup,
} from 'server/models/Listing/fieldgroups';

const {
  APP_URL,
  APP_REDIRECTION_DOMAIN_URL,
} = process.env;

export function makeApartmentUnitsFilter(searchFilters) {
  const bedrooms = searchFilters.bedrooms || [];
  const bathrooms = searchFilters.bathrooms || 0;
  const moveInMoment = searchFilters.moveInDate
    ? moment(searchFilters.moveInDate)
    : null;

  return (apartmentUnit) => {
    const bedroomCheck = bedrooms.length > 0
      ? bedrooms.includes(apartmentUnit.bedrooms)
      : true;
    return (
      bedroomCheck
      && apartmentUnit.bathrooms >= bathrooms
      && apartmentUnit.price <= searchFilters.maxRentPrice
      && apartmentUnit.price >= searchFilters.minRentPrice
      && (
        !searchFilters.availableHomesNearMoveInDate
        || !moveInMoment
        || !apartmentUnit.availableDate
        || moveInMoment.add(1, 'day').isAfter(apartmentUnit.availableDate)
      )
    );
  };
}

/**
 * Gets unit-specific listing fields to display in the listing card based on the
 * user's search filters and listing floor units for apartments, or base listing fields for non-apartments.
 *
 * @param  {Object} options.listing The listing.
 * @return {Object}                 Listing fields for display in the listing card.
 */
export function getListingFieldsForDisplay({ listing, searchFilters }) {
  typeCheck('listing::NonEmptyObject', listing);
  let listingFieldsForDisplay;
  if (listing.dataSource !== LISTING_DATA_SOURCE_SMART_APARTMENT) {
    listingFieldsForDisplay = {
      price: listing.price,
      bedrooms: listing.bedrooms,
      bathrooms: listing.bathrooms,
      squareFeet: listing.squareFeet,
      securityDeposit: listing.securityDeposit,
      petDeposit: listing.petDeposit,
    };
  }
  else {
    let filteredApartmentUnits = listing.apartmentUnits;
    // Filter out floor units that don't fit search params.
    if (searchFilters) {
      const apartmentUnitsFilter = makeApartmentUnitsFilter(searchFilters);
      filteredApartmentUnits = listing.apartmentUnits.filter(apartmentUnitsFilter);
      if (filteredApartmentUnits.length === 0) {
        filteredApartmentUnits = listing.apartmentUnits;
      }
    }
    const lastFilteredApartmentUnit = filteredApartmentUnits[filteredApartmentUnits.length - 1];

    let minUnitDeposit;
    let maxUnitDeposit;
    let minPrice;
    let maxPrice;

    filteredApartmentUnits.forEach((apartmentUnit) => {
      if (!minPrice || apartmentUnit.price < minPrice) {
        minPrice = apartmentUnit.price;
      }
      if (!maxPrice || apartmentUnit.price > maxPrice) {
        maxPrice = apartmentUnit.price;
      }
      if (!minUnitDeposit || apartmentUnit.deposit < minUnitDeposit) {
        minUnitDeposit = apartmentUnit.deposit;
      }
      if (!maxUnitDeposit || apartmentUnit.deposit < maxUnitDeposit) {
        maxUnitDeposit = apartmentUnit.deposit;
      }
    });
    const securityDepositRange = minUnitDeposit + (maxUnitDeposit !== minUnitDeposit
      ? ' - ' + maxUnitDeposit
      : ''
    );
    const petDepositRange = listing.petDeposit + (listing.petDepositMax !== listing.petDeposit
      ? ' - ' + listing.petDepositMax
      : ''
    );
    const priceRange = minPrice + (maxPrice !== minPrice
      ? ' - ' + maxPrice
      : ''
    );

    listingFieldsForDisplay = {
      price: priceRange,
      bedrooms: lastFilteredApartmentUnit.bedrooms,
      bathrooms: lastFilteredApartmentUnit.bathrooms,
      squareFeet: lastFilteredApartmentUnit.squareFeet,
      securityDeposit: securityDepositRange,
      petDeposit: petDepositRange,
    };
  }
  return listingFieldsForDisplay;
}

export function makeListingFullAddressString(listing = {}, opts) {
  return getListingFullAddressString({
    ...(listing || {}),
    addressStreetDisplay: makeListingAddressStreetDisplay(listing, opts),
  });
}

export function getListingCityStateString(listing) {
  return lodashCompact([
    (listing.addressCity || '').trim(),
    `${listing.addressStateCode || ''} ${listing.addressZip || ''}`.trim(),
  ]).join(', ');
}

export function getListingFullAddressString(listing) {
  const fullAddressStringParts = lodashCompact([
    (listing.addressStreetDisplay || '').trim(),
    getListingCityStateString(listing),
  ]);
  const fullAddressString = fullAddressStringParts.join(', ');
  return fullAddressString;
}

export function makeListingAddressStreetDisplay(listing, { showUnitWord, omitUnitNumber } = {}) {
  if (!listing || !listing.addressStreetName) {
    return null;
  }

  const addressStreet = [
    listing.addressStreetNumber || '',
    listing.addressStreetName,
    listing.addressStreetSuffix || '',
  ].join(' ').trim();

  const addressUnitNumber = listing.apartmentUnit || listing.addressUnitNumber;

  if (!addressUnitNumber || omitUnitNumber) {
    return addressStreet;
  }

  if (showUnitWord) {
    return `${addressStreet}, Unit ${addressUnitNumber}`;
  }

  return `${addressStreet} ${addressUnitNumber}`;
}

// eslint-disable-next-line no-useless-escape
export const addressStreetNumberRegex = /^\d+\w*\s*(?:(?:[\-\/]?\s*)?\d*(?:\s*\d+\/\s*)?\d+)?\s+/;

export function extractAddressStreetNumber(address) {
  const match = address.match(addressStreetNumberRegex);
  return match && match[0] && match[0].trim() || null;
}

export function getListingAgentPhone(listing) {
  return (
    listing.listingAgentCellPhone ||
    listing.listingAgentDirectWorkPhone ||
    ''
  );
}

export function getListingUrlPath(listing, queryParams) {
  typeCheck('listing::NonEmptyObject', listing);
  typeCheck('queryParams::Maybe Object', queryParams);

  let querySearch = queryParams ? `?${queryString.stringify(queryParams)}` : '';
  if (querySearch === '?') {
    querySearch = '';
  }

  return listing.dataSource === LISTING_DATA_SOURCE_SMART_APARTMENT
    ? `/listing/${listing.id}${querySearch}`
    : `/listing/${listing.id}/${getListingAddressSlug(listing, '/')}${querySearch}`;
}

export function getListingUrl(listing) {
  return `${APP_URL}${getListingUrlPath(listing)}`;
}

export function getListingApplyURL(listing, appUrl = APP_REDIRECTION_DOMAIN_URL) {
  typeCheck('listing::NonEmptyObject', listing);
  typeCheck('listingId::PositiveNumber', listing.id);
  return `${appUrl}/apply/${listing.id}`;
}

export function getListingAddressSlug(listing, separator) {
  typeCheck('listing::NonEmptyObject', listing);
  typeCheck('separator::Maybe String', separator);
  return [
    encodeURIComponent(slugify(convertStateCodeToStateName(listing.addressStateCode))),
    encodeURIComponent(slugify(listing.addressCity)),
    encodeURIComponent(listing.addressZip),
    encodeURIComponent(slugify(lodashCompact([
      listing.addressStreetName,
      listing.addressStreetNumber,
    ]).join('-').replace(/\//g, '-'))),
  ].join(separator || '/');
}

export function getListingSmartApartmentLink(listing) {
  return (
    'https://app.smartapartmentdata.com/ViewProperty.aspx' +
    `?propertyID=${encodeURIComponent(listing.smartApartmentId)}`
  );
}

export function getTourFromListing({ listingId, tours }) {
  tours = (tours || [])
    .filter((tour) => [
      TOUR_STATUS_REQUESTED,
      TOUR_STATUS_SCHEDULED,
      TOUR_STATUS_COMPLETED,
    ].includes(tour.status));

  for (const tour of tours) {
    for (const tourListing of tour.listings) {
      if (tourListing.id === listingId) {
        return tour;
      }
    }
  }
}

export function getShowingIsRequested({ listing, tours }) {
  return (tours || [])
    .filter((tour) => [TOUR_STATUS_REQUESTED].includes(tour.status))
    .reduce((acc, tour) => [...acc, ...tour.listings.map(({ id }) => id)], [])
    .includes(listing.id);
}

export function getShowingIsScheduled({ listing, tours }) {
  return (tours || [])
    .filter((tour) => [TOUR_STATUS_SCHEDULED].includes(tour.status))
    .reduce((acc, tour) => [...acc, ...tour.listings.map(({ id }) => id)], [])
    .includes(listing.id);
}

export function getShowingIsCompleted({ listing, tours }) {
  return (tours || [])
    .filter((tour) => [TOUR_STATUS_COMPLETED].includes(tour.status))
    .reduce((acc, tour) => [...acc, ...tour.listings.map(({ id }) => id)], [])
    .includes(listing.id);
}

export function getListingLocationPointString(location) {
  typeCheck('location::NonEmptyObject', location);

  const listingLocationPointIsValid = (
    typeof location.longitude === 'number'
    && !isNaN(location.longitude)
    && typeof location.latitude === 'number'
    && !isNaN(location.latitude)
  );

  const listingLocationPointString = listingLocationPointIsValid
    ? `Point(${location.longitude} ${location.latitude})`
    : null;

  return listingLocationPointString;
}

export function showMoveInDateWarning({ moveInDate, listingAvailableDate }) {
  const nowMoment = moment();
  const moveInDateMoment = moment(moveInDate);
  const availableDateMoment = moment(listingAvailableDate);

  return (
    availableDateMoment.isAfter(nowMoment)
    && (
      availableDateMoment.diff(moveInDateMoment, 'days') > LISTING_MOVE_IN_DATE_WARNING_THRESHOLD
      || moveInDateMoment.diff(availableDateMoment, 'days') > LISTING_MOVE_IN_DATE_WARNING_THRESHOLD
    )
  ) || (
    availableDateMoment.isBefore(nowMoment)
      && (
        moveInDateMoment.diff(nowMoment, 'days') > LISTING_MOVE_IN_DATE_WARNING_THRESHOLD
        || nowMoment.diff(moveInDateMoment, 'days') > LISTING_MOVE_IN_DATE_WARNING_THRESHOLD
      )
  );
}

export function shouldShowWarningMoveInDatePast({ moveInDate, listingAvailableDate }) {
  const nowMoment = moment();
  const moveInDateMoment = moment(moveInDate);
  const availableDateMoment = moment(listingAvailableDate);

  return (
    availableDateMoment.isBefore(nowMoment)
    && Math.abs(nowMoment.diff(moveInDateMoment, 'days')) > LISTING_MOVE_IN_DATE_WARNING_THRESHOLD
  );
}

export function shouldShowWarningMoveInDateTooFar({ moveInDate, listingAvailableDate }) {
  const nowMoment = moment();
  const moveInDateMoment = moment(moveInDate);
  const availableDateMoment = moment(listingAvailableDate);

  return (
    !availableDateMoment.isBefore(nowMoment)
    && moveInDateMoment.isAfter(availableDateMoment)
    && moveInDateMoment.diff(availableDateMoment, 'days') > LISTING_MOVE_IN_DATE_WARNING_THRESHOLD
  );
}

export function showWarningMoveInDateBeforeHomeAvailable({ moveInDate, listingAvailableDate }) {
  const moveInDateMoment = moment(moveInDate);
  const availableDateMoment = moment(listingAvailableDate);

  return moveInDateMoment.isBefore(availableDateMoment);
}

export function getListingCommissionAmount(listing) {
  const { compensationPackage } = listing;
  typeCheck('compensationPackage::NonEmptyArray', compensationPackage);
  return compensationPackage[0].commissionAmount || 0;
}

const sunroomPhotoUrlRegex = /^https:\/\/s3.amazonaws.com\/sr-listing-photos/;

/**
 * Checks if the provided photo URL is a Sunroom photo URL.
 *
 * @param  {string}  photoUrl The photo URL to check.
 * @return {Boolean}          `true` if the photo URL is a Sunroom photo URL.
 */
export function isSunroomPhotoUrl(photoUrl) {
  typeCheck('photoUrl::NonEmptyString', photoUrl);
  return sunroomPhotoUrlRegex.test(photoUrl);
}

/**
 * Generates the URL for sunroom listing photos based on a base sunroom photo URL,
 * a dimension string and a target resolution (1, 2, 3).
 *
 * @param  {string} options.imageUrl   Base image URL.
 * @param  {string} options.dimension  Dimension string. e.g. "100x500".
 * @param  {number} options.resolution Image resolution to use to fetch images in 1x, 2x, 3x. Defaults to 1.
 * @return {string}                    Sunroom listing photo URL with target dimension and resolution.
 */
export function generateSunroomListingPhotoUrl({ imageUrl, dimension, resolution = 1 }) {
  if (!imageUrl) {
    return '';
  }
  if (!dimension || !isSunroomPhotoUrl(imageUrl)) {
    return imageUrl;
  }
  typeCheck('imageUrl::NonEmptyString', imageUrl);
  typeCheck('dimension::NonEmptyString', dimension);
  typeCheck('resolution::Maybe PositiveNumber', resolution);

  const imageExtName = path.extname(imageUrl);
  const imageUrlWithoutExtName = imageUrl.replace(imageExtName, '');
  const dimensionWithoutQualityPart = `-${dimension.split(':')[0]}`;

  // E.g. "https://s3.amazonaws.com/sr-listing-photos/test/testing-image-200x240-3x.jpg"
  return `${imageUrlWithoutExtName}${dimensionWithoutQualityPart}-${resolution || 1}x${imageExtName}`;
}

export function getListingManagedPhotoUrls({
  listing,
  resolution,
  dimension,
  useWatermarks,
  includeHidden,
}) {
  if (!listing) {
    return [];
  }

  useWatermarks = typeof useWatermarks === 'boolean'
    ? useWatermarks
    : listing.useWatermarks || false;

  let sunroomPhotoUrls = (useWatermarks && listing.watermarkedPhotoUrls?.length > 0
    ? listing.watermarkedPhotoUrls
    : listing.sunroomPhotoUrls) || [];

  if (sunroomPhotoUrls.length === 0 && (listing.photoUrls || []).length > 0) {
    sunroomPhotoUrls = listing.photoUrls;
  }

  sunroomPhotoUrls = includeHidden
    ? sunroomPhotoUrls
    : sunroomPhotoUrls.filter((url) => !listing.hiddenPhotoUrls.includes(url));

  return sunroomPhotoUrls
    .map((photoUrl) => {
      return generateSunroomListingPhotoUrl({
        imageUrl: photoUrl,
        dimension: dimension,
        resolution: resolution,
      });
    });
}

/**
 * Makes sunroom photo base file name string.
 *
 * @param  {number} options.listingId      Listing ID.
 * @param  {string} options.sourcePhotoUrl Source photo URL to generate the image from.
 * @return {string}                        Sunroom photo file name.
 */
export function makeSunroomListingPhotoFileName({ listingId, sourcePhotoUrl, uniqueHash }) {
  typeCheck('listingId::PositiveNumber', listingId);
  typeCheck('sourcePhotoUrl::NonEmptyString', sourcePhotoUrl);
  typeCheck('uniqueHash::Maybe Boolean', uniqueHash);

  const photoUrlHash = shorthash.unique(uniqueHash ? uuidv4() : sourcePhotoUrl);
  const suffix = isFallbackImage(sourcePhotoUrl) ? '-fallback' : '';

  return `listing-${listingId}-${photoUrlHash}${suffix}.jpg`;
}

export function isFallbackImage(imageUrl) {
  typeCheck('imageUrl::NonEmptyString', imageUrl);
  return imageUrl.includes('fallback');
}

export function isListingTourable(listing) {
  typeCheck('listing::NonEmptyObject', listing);
  return (
    listing.status === LISTING_STATUS_ACTIVE
    && !listing.cantTourFlag
  );
}

export function isListingFullService(listing) {
  typeCheck('listing::NonEmptyObject', listing);
  return (
    !isListingTenantScreening(listing)
    && !isListingSunRise(listing)
  );
}

export function isListingTenantScreening(listing) {
  typeCheck('listing::NonEmptyObject', listing);
  return (
    listing.services?.includes(LISTING_SERVICE_TYPE_TENANT_SCREENING)
  );
}

export function isListingSunRise(listing) {
  typeCheck('listing::NonEmptyObject', listing);
  return (
    listing.services?.includes(LISTING_SERVICE_TYPE_SUNRISE)
  );
}

export function isListingPreLease(listing) {
  typeCheck('listing::NonEmptyObject', listing);
  return (
    listing.services?.includes(LISTING_SERVICE_TYPE_PRE_LEASE)
  );
}

export function getListingServiceType(listing) {
  if (isListingTenantScreening(listing)) {
    return 'SunScreen';
  }
  if (isListingSunRise(listing)) {
    return 'SunSetup';
  }

  return 'Full-Service';
}

export function getListingAvailableDateMoment(listing) {
  const listingAvailableDate = moment.utc(listing.availableDate).format('MM/DD/YYYY');
  return momentWithAustinTimezone(new Date(listingAvailableDate));
}

export function getListingAvailableNowLabel(listing) {
  const listingAvailableDateMoment = getListingAvailableDateMoment(listing);
  return moment().isAfter(listingAvailableDateMoment)
    ? 'Available Now'
    : listingAvailableDateMoment.format('[Available] MMM D');
}

export function getListingAvailableDateRange({ listing, tenantScreeningCriteria = {} }) {
  typeCheck('listing::Object', listing);
  const moveInDateRangeInDays = tenantScreeningCriteria.moveInDateRangeInDays || 30;

  const todayMoment = momentWithAustinTimezone().startOf('day');
  const listingAvailableDateMoment = getListingAvailableDateMoment(listing).format();
  const startDate = moment(listingAvailableDateMoment).isAfter(todayMoment)
    ? momentWithAustinTimezone(listingAvailableDateMoment)
    : momentWithAustinTimezone(todayMoment);

  if (
    !isListingTenantScreening(listing)
    && !isListingPreLease(listing)
    && tenantScreeningCriteria.moveInMinimumDays
  ) {
    startDate.add(tenantScreeningCriteria.moveInMinimumDays, 'days');
  }

  const endDate = momentWithAustinTimezone(startDate).add(moveInDateRangeInDays, 'days');
  const extendDate = momentWithAustinTimezone(endDate).add(LISTING_MOVE_IN_DATE_EXTENDED_DAYS, 'days');

  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    extendedDate: extendDate.toISOString(),
  };
}

export function existsPolicyPayment(listing) {
  return [
    'policyElectricPaid',
    'policyGasPaid',
    'policyTrashPaid',
    'policyWaterPaid',
    'policyPestControlpaid',
    'policyGardeningPaid',
  ].some((field) => {
    return typeof listing[field] === 'boolean';
  });
}

export function existsPetData(listing) {
  return [
    'maximumPetsAllowed',
    'petWeight',
    'petBreedRestriction',
  ].some((field) => {
    return listing[field] !== null;
  });
}

export function getListingPetWeightLimit(listing) {
  const petDescription = listing.petDescription || '';
  let petWeightLimit = listing.petWeight || null;

  if (petDescription.includes('Weight Limit - Extra Large')) {
    petWeightLimit = 75;
  }
  else if (petDescription.includes('Weight Limit - Large')) {
    petWeightLimit = 50;
  }
  else if (petDescription.includes('Weight Limit - Medium')) {
    petWeightLimit = 35;
  }
  else if (petDescription.includes('Weight Limit - Small')) {
    petWeightLimit = 20;
  }

  return petWeightLimit;
}

export function hasListingPetWeightLimit(petWeightLimit) {
  return petWeightLimit < PET_WEIGHT_OPTION_ITEM_NONE;
}

export function makeListingsFilters({ user, market }) {
  typeCheck('user::NonEmptyObject', user);
  typeCheck('market::NonEmptyObject', market);

  const searchFiltersDisabled = (market.settings.searchFilters || {}).disabled || [];

  const listingFilters = {
    ...user.searchFilters,
    locationPoint: user.searchFilters.area
      ? [...user.searchFilters.area, user.searchFilters.area[0]]
      : undefined,
  };

  return lodashOmit(searchFiltersDisabled, listingFilters);
}

export function getListingAmenities(listing) {
  const laundryDescription = listing.laundryDescription || '';

  const listingAmenities = LISTING_AMENITIES_LIST.reduce((acc, amenityName) => {
    const amenity = {
      name: amenityName,
      emoji: LISTING_AMENITIES_SETTINGS[amenityName].emoji,
      iconClassName: LISTING_AMENITIES_SETTINGS[amenityName].iconClassName,
      title: LISTING_AMENITIES_SETTINGS[amenityName].title,
      value: !!listing[amenityName],
      rentlinxAmenityId: LISTING_AMENITIES_SETTINGS[amenityName].rentlinxAmenityId,
    };
    switch (amenityName) {
      case 'hasCommonFacilitiesWasherDryer':
        amenity.value = (
          !listing.hasWasherDryer
          && laundryDescription.includes('Common Facilities')
        );
        break;
      case 'washerDryerConnections':
        amenity.value = (
          (!listing.hasWasher || !listing.hasDryer)
          && !laundryDescription.includes('Common Facilities')
          && listing.hasWasherDryerConnections
        );
        break;
      case 'hasWasherDryer':
        amenity.value = (
          (listing.hasWasherDryer
          || (
            listing.hasWasher && listing.hasDryer
          ))
          && !listing.hasStackedWasherDryer
        );
        break;
      case 'hasStackedWasherDryer':
        amenity.value = (
          listing.hasStackedWasherDryer
        );
        break;
      case 'hasWasher':
        amenity.value = (
          listing.hasWasher
            && !listing.hasDryer
            && !listing.hasStackedWasherDryer
            && !listing.hasWasherDryer
        );
        break;
      case 'hasDryer':
        amenity.value = (
          !listing.hasWasher
            && listing.hasDryer
            && !listing.hasStackedWasherDryer
            && !listing.hasWasherDryer
        );
        break;
      case 'hasWasherDryerConnections':
        amenity.value = (
          (!listing.hasWasher && !listing.hasDryer)
          && listing.hasWasherDryerConnections
        );
        break;
      case 'parkingSpaces':
        if (listing.parkingSpaces) {
          amenity.title = pluralize(amenity.title, listing.parkingSpaces, true);
        }
        break;
      case 'hasGarage':
        if (listing.garageSpaces) {
          amenity.title = `${listing.garageSpaces} garage ${pluralize('space', listing.garageSpaces)}`;
        }
        break;
      case 'petsAllowed':
        amenity.value = !!(listing.dogsAllowed && listing.catsAllowed);
        break;
      case 'catAllowed':
        amenity.value = listing.catsAllowed && !listing.dogsAllowed;
        break;
      case 'dogsAllowed':
        amenity.value = listing.dogsAllowed && !listing.catsAllowed;
        break;
      case 'noPets':
        amenity.value = (
          isBoolean(listing.dogsAllowed)
          && isBoolean(listing.catsAllowed)
          && !listing.dogsAllowed
          && !listing.catsAllowed
        );
        break;
      case 'noCarpet':
        amenity.value = isBoolean(listing.hasCarpetFlooring) && !listing.hasCarpetFlooring;
        break;
    }

    acc.push(amenity);

    return acc;
  }, []);

  return listingAmenities;
}

export function getDbEnabledListingAmenities(listing) {
  return booleanAmenitiesFieldgroup.reduce((acc, amenityName) => [
    ...acc,
    ...listing[amenityName] ? [amenityName] : [],
  ], []);
}

export function getListingAppliances(listing) {
  const appliances = [
    {
      label: 'Refrigerator',
      enabled: listing.hasRefrigerator,
      submittedDate: listing.hasRefrigerator
        ? listing.refrigeratorPhotoSubmittedDate || listing.hasRefrigeratorSubmittedDate
        : listing.hasRefrigeratorSubmittedDate,
      coverPhoto: (listing.refrigeratorPhotoUrls || [])[0],
      forLeaseHelper: true,
    },
    {
      label: 'Gas Stove',
      enabled: listing.hasGasStove,
      submittedDate: listing.hasGasStove
        ? listing.gasStovePhotoSubmittedDate || listing.hasGasStoveSubmittedDate
        : listing.hasGasStoveSubmittedDate,
      coverPhoto: (listing.gasStovePhotoUrls || [])[0],
      forLeaseHelper: true,
    },
    {
      label: 'Electric Stove',
      enabled: listing.hasElectricStove,
      submittedDate: listing.hasElectricStove
        ? listing.electricStovePhotoSubmittedDate || listing.hasElectricStoveSubmittedDate
        : listing.hasElectricStoveSubmittedDate,
      coverPhoto: (listing.electricStovePhotoUrls || [])[0],
      forLeaseHelper: true,
    },
    {
      label: 'Dishwasher',
      enabled: listing.hasDishWasher,
      submittedDate: listing.hasDishWasher
        ? listing.dishWasherPhotoSubmittedDate || listing.hasDishWasherSubmittedDate
        : listing.hasDishWasherSubmittedDate,
      coverPhoto: (listing.dishWasherPhotoUrls || [])[0],
      forLeaseHelper: true,
    },
    {
      label: 'Microwave',
      enabled: listing.hasMicrowave,
      submittedDate: listing.hasMicrowave
        ? listing.microwavePhotoSubmittedDate || listing.hasMicrowaveSubmittedDate
        : listing.hasMicrowaveSubmittedDate,
      coverPhoto: (listing.microwavePhotoUrls || [])[0],
      forLeaseHelper: true,
    },
    {
      label: 'Washer',
      enabled: listing.hasWasher || listing.hasStackedWasherDryer,
      submittedDate: listing.hasWasher
        ? listing.washerPhotoSubmittedDate || listing.hasWasherSubmittedDate
        : listing.hasWasherSubmittedDate,
      coverPhoto: (listing.washerPhotoUrls || [])[0],
      forLeaseHelper: true,
    },
    {
      label: 'Dryer',
      enabled: listing.hasDryer || listing.hasStackedWasherDryer,
      submittedDate: listing.hasDryer
        ? listing.dryerPhotoSubmittedDate || listing.hasDryerSubmittedDate
        : listing.hasDryerSubmittedDate,
      coverPhoto: (listing.dryerPhotoUrls || [])[0],
      forLeaseHelper: true,
    },
    {
      label: 'Washer/Dryer Connections',
      enabled: listing.hasWasherDryerConnections,
      submittedDate: listing.hasWasherDryerConnections
        ? listing.washerDryerConnectionsPhotoSubmittedDate || listing.hasWasherDryerConnectionsSubmittedDate
        : listing.hasWasherDryerConnectionsSubmittedDate,
      coverPhoto: (listing.washerDryerConnectionsPhotoUrls || [])[0],
      forLeaseHelper: false,
    },
  ];
  return appliances;
}

export function getAppliancesPhotos(listing) {
  const appliances = getListingAppliances(listing).filter((applianceProps) => {
    const hidden = (
      typeof applianceProps.enabled !== 'boolean'
      || !applianceProps.enabled
      || !applianceProps.submittedDate
    );
    return !hidden;
  }).map((appliance) => appliance.coverPhoto)
    .filter((coverPhoto) => coverPhoto);
  return appliances;
}

export function getMarketingItemsPhotos(listing) {
  const marketingItems = [
    listing.lockboxPhotoUrls,
    listing.comboPhotoUrls,
    listing.yardSignPhotoUrls,
    listing.sunlockPhotoUrls,
    listing.keyPickupPhoto,
  ];
  return marketingItems.filter((itemArray) => !!itemArray && itemArray.length).map((item) => item[0]);
}

export function getActiveListingGroupAmenities(listings) {
  const allListingAmenities = [];
  const activeListingAmenitiesMapping = {};

  for (const listing of listings) {
    for (const listingAmenity of getListingAmenities(listing)) {
      if (listingAmenity.value && !activeListingAmenitiesMapping[listingAmenity.name]) {
        allListingAmenities.push(listingAmenity);
        activeListingAmenitiesMapping[listingAmenity.name] = true;
      }
    }
  }
  return allListingAmenities;
}

export function isSelfShowingListingAvailableToVisit() {
  const now = momentWithAustinTimezone();
  return now.hour() >= 8 && now.hour() < 20;
}

export function getLeaseLengthDescription({ maxLeaseMonths, minLeaseMonths }) {
  let leaseLengthLabel = 'Lease Length Min/Max.';
  let leaseLengthValue = maxLeaseMonths && minLeaseMonths
    ? `${minLeaseMonths} - ${maxLeaseMonths} Months`
    : '--';

  if (!minLeaseMonths && maxLeaseMonths) {
    leaseLengthLabel = 'Lease Length Max.';
    leaseLengthValue = `${maxLeaseMonths} Months`;
  }
  else if (!maxLeaseMonths && minLeaseMonths) {
    leaseLengthLabel = 'Lease Length Min.';
    leaseLengthValue = `${minLeaseMonths} Months`;
  }
  else if (minLeaseMonths === maxLeaseMonths) {
    leaseLengthValue = `${minLeaseMonths} Months`;
  }

  return {
    leaseLengthLabel,
    leaseLengthValue,
  };
}

export function getDaysOnMarket(listing) {
  const lastActiveDate = !listing.lastActiveDate ? null : momentWithAustinTimezone(listing.lastActiveDate);
  const lastLeasedDate = !listing.lastLeasedDate ? null : momentWithAustinTimezone(listing.lastLeasedDate);
  const isListingActive = listing.status === LISTING_STATUS_ACTIVE;
  const calculationDate = isListingActive
    ? momentWithAustinTimezone()
    : lastLeasedDate || null;

  const daysOnMarket = (typeof listing.mlsDaysOnMarket === 'number'
    ? listing.mlsDaysOnMarket
    : (lastActiveDate && calculationDate
      ? Math.floor((calculationDate.unix() - lastActiveDate.unix()) / 86400)
      : null
    )
  );

  return daysOnMarket;
}

export function getListingFieldsMismatch(listing1, listing2, fieldgroup) {
  typeCheck('fieldgroup::Maybe Array', fieldgroup);
  let listing1Subset = { ...listing1 };
  let listing2Subset = { ...listing2 };

  if (fieldgroup) {
    listing1Subset = pickWithUndefinedDefault(
      fieldgroup,
      listing1,
      null,
    );
    listing2Subset = pickWithUndefinedDefault(
      fieldgroup,
      listing2,
      null,
    );
  }

  listing1Subset.availableDate = listing1Subset.availableDate
    ? (
      momentWithAustinTimezone(listing1Subset.availableDate)
        .add(12, 'hours')
        .startOf('day')
        .toISOString()
    )
    : null;

  listing2Subset.availableDate = listing2Subset.availableDate
    ? (
      momentWithAustinTimezone(listing2Subset.availableDate)
        .add(12, 'hours')
        .startOf('day')
        .toISOString()
    )
    : null;

  listing1Subset.numberOfPhotos = (listing1Subset.photoUrls || []).length;
  delete listing1Subset.photoUrls;

  listing2Subset.numberOfPhotos = (listing2Subset.photoUrls || []).length;
  delete listing2Subset.photoUrls;

  // https://sunroom.atlassian.net/browse/PD-2094
  const listings = [listing1Subset, listing2Subset];
  const listingWithAptUnit = listings.find(({ apartmentUnit }) => apartmentUnit);
  const listingWithoutAptUnit = listings.find(({ apartmentUnit }) => !apartmentUnit);
  const fieldsMismatch = [];

  if (listingWithAptUnit && listingWithoutAptUnit) {
    listingWithoutAptUnit.apartmentUnit = listingWithoutAptUnit.addressStreetDisplay.toLowerCase();
    const apartmentUnitRegex = new RegExp(` ${listingWithAptUnit.apartmentUnit.toLowerCase()}$`);
    if (!apartmentUnitRegex.test(listingWithoutAptUnit.apartmentUnit)) {
      fieldsMismatch.push({
        fieldId: 'apartmentUnit',
        listing1Value: listing1Subset.apartmentUnit,
        listing2Value: listing2Subset.apartmentUnit,
      });
    }
  }
  delete listing1Subset.dataSource;
  delete listing1Subset.apartmentUnit;
  delete listing1Subset.addressStreetDisplay;
  delete listing2Subset.dataSource;
  delete listing2Subset.apartmentUnit;
  delete listing2Subset.addressStreetDisplay;

  const diff = objectDiff(listing1Subset, listing2Subset);

  if (diff.changed !== 'equal') {
    for (const key of Object.keys(diff.value)) {
      if (diff.value[key].changed !== 'equal') {
        fieldsMismatch.push({
          fieldId: key,
          listing1Value: listing1Subset[key],
          listing2Value: listing2Subset[key],
        });
      }
    }
  }
  return fieldsMismatch;
}

export function getIgloohomeCodeFormat(code) {
  if (!code) {
    return '';
  }

  if (code.length === 9) {
    return `${code.substr(0, 3)} ${code.substr(3, 3)} ${code.substr(6)}`;
  }
  if (code.length === 7) {
    return `${code.substr(0, 3)} ${code.substr(3)}`;
  }

  return code;
}

export function isSelfShowingListing(listing) {
  if (!listing) {
    return false;
  }
  return !!(
    listing.sunlockId
    || listing.igloohomeLockId
    || listing.codeboxLockId
    || listing.electronicLockCode
    || listing.sunlockRaspberryPiSerialNumber
    || false
  );
}

/**
 * Get Self Showing Lock Type.
 *
 * @param {Object} listing  Listing to determine the lock type.
 * @return {string} The self showing lock type.
 */
export function getListingSelfShowingLockType(listing) {
  if (listing?.igloohomeLockId) {
    return SELF_SHOWING_LOCK_TYPE_IGLOOHOMELOCK;
  }
  if (listing?.sunlockId) {
    return SELF_SHOWING_LOCK_TYPE_SUNLOCK;
  }
  if (listing?.codeboxLockId) {
    return SELF_SHOWING_LOCK_TYPE_CODEBOX;
  }

  return null;
}

// WARNING(@d-oliveros): listing.has3dTour is a calculated field, it is not part of the listing schema.
export function hasListing3DTour(listing) {
  return !!listing.has3dTour;
}

export function getListingsMinMaxValues(listings) {
  let minPrice;
  let maxPrice;
  let minSquareFeet;
  let maxSquareFeet;

  listings.forEach((listing) => {
    if (!minPrice || listing.price < minPrice) {
      minPrice = listing.price;
    }
    if (!maxPrice || listing.price > maxPrice) {
      maxPrice = listing.price;
    }
    if (!minSquareFeet || listing.squareFeet < minSquareFeet) {
      minSquareFeet = listing.squareFeet;
    }
    if (!maxSquareFeet || listing.squareFeet > maxSquareFeet) {
      maxSquareFeet = listing.squareFeet;
    }
  });

  return {
    minPrice,
    maxPrice,
    minSquareFeet,
    maxSquareFeet,
  };
}

export function getSquareFeetRangeDisplay(listings) {
  const minMaxValues = getListingsMinMaxValues(listings);
  if (minMaxValues.minSquareFeet === minMaxValues.maxSquareFeet) {
    return minMaxValues.minSquareFeet;
  }
  return `${minMaxValues.minSquareFeet} - ${minMaxValues.maxSquareFeet}`;
}

export function makeListingPhotosFromFloorPlan({ photoUrls, photoMappings }) {
  typeCheck('photoUrls::Array', photoUrls);
  typeCheck('photoMappings::Object', photoMappings);

  if (!Object.keys(photoMappings).length) {
    return {
      photoUrls,
      photoMappings,
      sunroomPhotoUrls: photoUrls,
    };
  }

  const photoUrlToProcessedPhotoUrlMappings = lodashInvert(photoMappings);

  return {
    photoUrls,
    photoMappings,
    sunroomPhotoUrls: photoUrls
      .map((photoUrl) => photoUrlToProcessedPhotoUrlMappings[photoUrl])
      .filter((x) => x),
  };
}

export function isSunroomListing(listing) {
  return listing.dataSource === LISTING_DATA_SOURCE_SUNROOM;
}

export function makePartnerlistingShareLink({ appUrl, listingId, partnerSlug }) {
  const queryString = (
    '?utm_source=sunroom&utm_medium=textlink' +
    '&utm_campaign=share-this-page&utm_content=copy-link'
  );
  return url.resolve(appUrl || '', `/partner/${partnerSlug}/listing/${listingId}${queryString}`);
}

export function getSecurityDepositSettings({ listing, partner }) {
  typeCheck('listing::NonEmptyObject', listing);
  typeCheck('partner::Maybe NonEmptyObject', partner);
  if (
    !partner
    || listing.whoCollectsSecurityDepositPetFees === LISTING_WHO_COLLECTS_LISTING_FEES_VALUE_MANUAL
    || (
      listing.whoCollectsSecurityDepositPetFees === LISTING_WHO_COLLECTS_LISTING_FEES_VALUE_SUNROOM
      && listing.srStripeAccountId
    )
  ) {
    return pickPartnerListingFeeSettingsFieldgroup(listing);
  }
  return pickPartnerListingFeeSettingsFieldgroup(partner);
}

export function isSingleFamilyListing(listing) {
  return PROPERTY_TYPES_SINGLE_FAMILY.includes(listing.propertyType);
}

export function getOwnerPaysForFields(listing) {
  typeCheck('listing::NonEmptyObject', listing);

  const ownerPaysForFields = ownerPaysForFieldgroup.reduce((memo, fieldName) => {
    if (typeof listing[fieldName] === 'boolean') {
      memo[fieldName] = listing[fieldName];
    }
    return memo;
  }, {});

  return ownerPaysForFields;
}

export function getPropertyAccessCode(listing) {
  switch (listing.propertySetupAccessType) {
    case PROPERTY_SETUP_ACCESS_TYPE_USE_ACCESS_CODE:
      return listing.propertyAccessCode;
    case PROPERTY_SETUP_ACCESS_TYPE_COMBO_LOCKBOX:
      return listing.lockboxCode;
    case PROPERTY_SETUP_ACCESS_TYPE_SUPRA_LOCKBOX:
      return listing.supraCbsCode;
    default:
      return null;
  }
}

export function getPropertyAccessContactUser({ listing, createdByUser, landlord }) {
  if (listing?.propertySetupAccessType === PROPERTY_SETUP_ACCESS_TYPE_CONTACT_FOR_KEY) {
    return {
      name: listing.propertyAccessContactName,
      phone: listing.propertyAccessContactPhone,
    };
  }

  if (listing?.listingAgreementSignedBy === MARKETING_PARTNER_LISTING_AGREEMENT_SIGNED_BY_SUNROOM
    && landlord !== null
  ) {
    // Hands-off listing
    return {
      name: landlord.name,
      phone: landlord.phone,
    };
  }

  return {
    name: createdByUser?.name,
    phone: createdByUser?.phone,
  };
}

export function removeDuplicatesByMlsNumber(listings) {
  typeCheck('listings::Array', listings);

  const sortedListings = [...listings];
  sortedListings.sort((listing1, listing2) => {
    return moment(listing1.createdDate).diff(listing2.createdDate) > 0 ? -1 : 1;
  });

  const mlsNumberExists = {};
  return sortedListings.filter((listing) => {
    if (!mlsNumberExists[listing.mlsNumber]) {
      mlsNumberExists[listing.mlsNumber] = true;
      return true;
    }
    return false;
  });
}

export function removeDuplicatesByFullAddress(listings) {
  typeCheck('listings::Array', listings);

  const sortedListings = [...listings];
  sortedListings.sort((listing1, listing2) => {
    return moment(listing1.createdDate).diff(listing2.createdDate) > 0 ? -1 : 1;
  });

  const addressExists = {};
  return sortedListings.filter((listing) => {
    const fullAddress = makeListingFullAddressString(listing);
    if (!addressExists[fullAddress]) {
      addressExists[fullAddress] = true;
      return true;
    }
    return false;
  });
}

export function getMoveInAccessCode({
  lockboxCode,
  propertyAccessCode,
  propertySetupAccessType,
  sunlockLockCode,
}) {
  let code = '';
  let accessType = '';

  if (sunlockLockCode) {
    code = sunlockLockCode;
    accessType = 'sunlock';
  }
  else if (
    propertySetupAccessType === PROPERTY_SETUP_ACCESS_TYPE_USE_ACCESS_CODE
    && propertyAccessCode
  ) {
    code = propertyAccessCode;
    accessType = 'lockbox';
  }
  else if (lockboxCode) {
    code = propertyAccessCode;
    accessType = 'comboLockbox';
  }

  return {
    code,
    accessType,
  };
}

export function getProratedRentMonth(rentPrice) {
  const daysInMonth = moment().daysInMonth();
  const daysRemaining = moment().endOf('month').diff(moment(), 'days');

  if (daysRemaining === 0) {
    return rentPrice;
  }

  const proratedRent = rentPrice / daysInMonth * daysRemaining;

  return proratedRent;
}

export function shouldHideSunlockCodes(listing, user) {
  return (
    hasRoleAdmin(user.roles)
    && (listing.status === LISTING_STATUS_ACTIVE || listing.status === LISTING_STATUS_PENDING)
  );
}

export function getMarketSecurityDepositLabel(market, capitalize) {
  let securityDepositLabel = market?.securityDepositLabel || 'security deposit';

  if (capitalize) {
    securityDepositLabel = capitalizeAll(securityDepositLabel);
  }

  return securityDepositLabel;
}

export function getCLPLink({ listingId }) {
  typeCheck('listingId::PositiveNumber', listingId);

  return `${APP_URL}/admin/listings/edit/${listingId}`;
}

export function getPLPLink({ partnerSlug, listingId }) {
  typeCheck('partnerSlug::NonEmptyString', partnerSlug);
  typeCheck('listingId::PositiveNumber', listingId);

  return `${APP_URL}/partner/${partnerSlug}/listing/${listingId}`;
}

export function getListingOrdersLink({ listingId }) {
  typeCheck('listingId::PositiveNumber', listingId);

  return `${APP_URL}/sunsetup/listing-orders/${listingId}`;
}

export function isPreLeaseActive({ listing }) {
  // Listing needs to be a pre-lease listings
  if (!isListingPreLease(listing)) {
    return false;
  }
  // A pre-lease listings stops being active if a self-showing lock is active
  if (isSelfShowingListing(listing)) {
    return false;
  }
  // If the listing does not have a self-showing lock and it was marketed as
  // a vacant property, validate that the vacant date is in the past so the pre-lease
  // stops being marked as active.
  if (listing.occupantType !== LISTING_OCCUPANT_TYPE_VACANT) {
    return moment(listing.vacantDate).isAfter(moment());
  }

  return true;
}
